import React, {useCallback, useEffect, useState} from 'react';
import {withRouter} from 'react-router';
import {withTranslation} from 'react-i18next';
import classNames from 'classnames';
import './index.scss';

import RoleManager from '../../../services/roleManage';
import {defaultPromiseResolve, isSameObjects, moveArrayItem} from '../../../utils/objects-util';
import {CONTROL_TYPE, getCurrencyIcons, ROLES_MANAGER_TYPE} from '../../../utils/enums';

import SectionItemEditControl from '../SectionItemEditControl';
import IconButton from '../IconButton';
import LinkControl from '../LinkControl';
import ConfirmDialog from '../../containers/ConfirmDialogContainer';
import IconWithQuantity from '../IconWithQuantity';
import preloader from '../../../assets/images/preloader.GIF';
import SectionItemText from '../SectionItemText/index.js';
import SectionItemPercent from '../SectionItemPercent/index.js';
import SwitchControl from '../SwitchControl';
import AutoSizer from "react-virtualized-auto-sizer";
import {List} from "react-virtualized";

const EditableTable = ({
  t,
  history,
  model,
  items,
  onPropChange,
  onLayout,
  onDelete,
  onDrop,
  isTranslationRtl,
  handleOnClick,
  isLoading,
  virtualized
}) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(null);

  const handleOnPropChange = (propValue, column) => {
    onPropChange(propValue, column);
  };

  const handleUpdateItemsValue = (propValue, column, newItem) => {
    const newItems = items.map(item => {
      if (isSameObjects(item, newItem)) {
        const updatedItem = {
          ...item,
          [column.accessor]: propValue[column.accessor] || '',
        };
        if(column.markWhenDirty) {
          updatedItem[`${column.accessor}-isDirty`] = true;
        }
        return updatedItem;
      } else {
        return item;
      }
    });

    onPropChange({ values: newItems }, column);
  };

  // Boolean twin of handleUpdateItemsValue: that one does `propValue[accessor] || ''`,
  // which would silently turn `false` into '' — so switches get their own path.
  const handleUpdateItemsBoolValue = (value, column, newItem) => {
    const newItems = items.map(item => {
      if (isSameObjects(item, newItem)) {
        const updatedItem = {
          ...item,
          [column.accessor]: value,
        };
        if(column.markWhenDirty) {
          updatedItem[`${column.accessor}-isDirty`] = true;
        }
        return updatedItem;
      } else {
        return item;
      }
    });

    onPropChange({ values: newItems }, column);
  };

  const handleOnLinkClick = (column, item) => {
    return new Promise(resolve => {
      resolve();
      history.push(column.link || column.url(item), { some: 'state' });
    });
  };

  const handleOnClickIconWithQuantity = (column, item) => {
    if (column.isLayout) {
      onLayout(column.url(item), item);
    } else {
      history.push(column.url(item));
    }
  };

  const getItem = (column, item) => {
    if (column.component) {
      return column.component(item);
    } else if (column.isEditable) {
      return (
        <SectionItemEditControl
          id={item[model.key]}
          column={{...column, isReadonly: model.isReadonly || column.isReadonly}}
          item={item}
          onPropChange={propValue => handleOnPropChange(propValue, column)}
        />
      );
    } else if (column.type === CONTROL_TYPE.linkControl) {
      return (
        <LinkControl
          column={{ title: item[column.accessor], isLayout: column.isLayout, isReadonly: model.isReadonly || column.isReadonly }}
          value={column.url(item)}
          onClick={() => onLayout(column.url(item), item)}
        />
      );
    } else if (column.type === CONTROL_TYPE.iconWithQuantity) {
      return (
        <IconWithQuantity
          onClick={() => handleOnClickIconWithQuantity(column, item)}
          item={item}
          column={column}
          onLayout={onLayout}
        />
      );
    } else if (column.type === CONTROL_TYPE.link && (!column.isShow || column.isShow(item))) {
      return (
        <IconButton
          isIcon={true}
          iconName={column.iconName}
          tooltipText={column.tooltipText}
          onClick={() => handleOnLinkClick(column, item)}
        />
      );
    } else if ((column.type === CONTROL_TYPE.inputText || column.type === CONTROL_TYPE.inputNumber) && (!column.isShow || column.isShow(item))) {
      let result = 'text';
      switch (column.type) {
        case CONTROL_TYPE.inputNumber:
          result = 'number'; break;
        default:
          result = 'text';
      }
      return (
          <div className={classNames("tableInputText", {directionRight: isTranslationRtl})}>
        <SectionItemText
          type={result}
          column={{
            ...column,
            isReadonly: model.isReadonly || column.isReadonly,
            // a function placeholder is resolved per row (e.g. the row's base price)
            placeholder: typeof column.placeholder === 'function' ? column.placeholder(item) : column.placeholder,
          }}
          item={item}
          withTitle={false}
          value={item[column.accessor] ?? ''}
          onPropChange={value => handleUpdateItemsValue(value, column, item)}
        />
          </div>
      );
    } else if (column.type === CONTROL_TYPE.inputPercent && (!column.isShow || column.isShow(item))) {

    return (
      <div className={classNames('tableInputPercent', {directionRight: isTranslationRtl})}>
        <SectionItemPercent
          column={column}
          item={item}
          allItems={items}
          withReset={item.withResetIcon}
          withTitle={false}
          value={(item[column.accessor] || item[column.accessor] === 0) ? item[column.accessor] : null}
          onPropChange={value => handleUpdateItemsValue(value, column, item)}
        />
      </div>
    );
  } else if (column.type === CONTROL_TYPE.switch && (!column.isShow || column.isShow(item))) {
      return (
        <div className={classNames('tableSwitchControl')}>
          <SwitchControl
            value={!!item[column.accessor]}
            isReadonly={model.isReadonly || column.isReadonly}
            isNoRtl={column.isNoRtl}
            onlyParentState
            onClick={value => handleUpdateItemsBoolValue(value, column, item)}
          />
        </div>
      );
  } else if (column.type === CONTROL_TYPE.text && (!column.isShow || column.isShow(item))) {
      let value = item[column.accessor]
      const ILSSymbol = getCurrencyIcons('ILS')
      if(typeof value === 'string' && value?.includes(ILSSymbol)) {
        value = value.replace(ILSSymbol, '') + ` ${ILSSymbol}`
      }

      return <div className={classNames('contentText')}>{value}</div>;
    } else {
      return typeof item[column.accessor] === 'string'
        ? item[column.accessor].replace(/<[^>]*>|&nbsp;|&amp;/g, '')
        : item[column.accessor];
    }
  };

  const handleOnDragEnter = index => {
    if (model && model.isDraggable) {
      setDragIndex(index);
    }
  };

  const handleOnDragStart = (e, item) => {
    if (model && model.isDraggable) {
      e.target.style.opacity = '0.4';
      setDragItem(item);
    }
  };

  const handleOnDragEnd = e => {
    if (model && model.isDraggable) {
      e.target.style.opacity = '1';
    }
  };

  const handleOnDrop = item => {
    if (model && model.isDraggable) {
      onDrop(moveArrayItem(items, dragItem, item, 'order', 'id'));
      setDragItem(null);
      setDragIndex(null);
    }
  };

  const handleOnDragOver = useCallback(e => {
    e.preventDefault();
  }, []);

  const handleOnDelete = id => {
    return new Promise(resolve => {
      resolve();
      setShowConfirmDialog(id);
    });
  };

  const handleOnConfirmDialog = token => {
    return new Promise(resolve => {
      onDelete(showConfirmDialog);
      setShowConfirmDialog(null);
      resolve();
    });
  };

  const handleOnCancelConfirmDialog = () => {
    return new Promise(resolve => {
      resolve();
      setShowConfirmDialog(null);
    });
  };

  useEffect(() => {
    if (window) {
      window.addEventListener('dragover', handleOnDragOver);
    }

    return () => {
      window.removeEventListener('dragover', handleOnDragOver);
    };
  }, [handleOnDragOver]);

  const rowRender = ({index, key, style}) => {
    const item = items[index];
    return (
      <table
        id={key}
        key={key}
        style={style}
      >
        <thead></thead>
        <tbody>
          <tr
            draggable={model.isDraggable}
            className={classNames({ dropable: index === dragIndex })}
            onDragEnter={() => handleOnDragEnter(index)}
            onDragStart={e => handleOnDragStart(e, item)}
            onDragEnd={handleOnDragEnd}
            onDrop={() => handleOnDrop(item)}
          >
            {model.isDraggable && (
                <td style={{ width: '40px' }}>
                  <IconButton
                      isIcon={true}
                      iconName={'move'}
                      tooltipText={t('basic.MOVE_ITEM_LABEL')}
                      onClick={defaultPromiseResolve}
                  />
                </td>
            )}
            {model.columns.map((column, columnIndex) => (
                <td
                    key={`item-${column.accessor}-${columnIndex}`}
                    style={{ width: column.width ? `${column.width}` : `auto` }}
                    // `dirty` only lets a screen style edited cells; nothing is styled by default.
                    className={classNames('overflowHiddenText', {
                      dirty: column.markWhenDirty && item.hasOwnProperty(`${column.accessor}-isDirty`),
                    })}
                    onClick={columnIndex === 0 && handleOnClick ? () => handleOnClick() : null}
                >
                  {getItem(column, item, columnIndex)}
                </td>
            ))}
            {model.showDeleteButton &&
                RoleManager.instance.isAuthorized(
                    ROLES_MANAGER_TYPE[CONTROL_TYPE.linkIconComponent],
                    model.typeModel + 'DeleteButton'
                ) && (
                    <td className={classNames('dialogContainer')} style={{ width: '40px' }}>
                      <IconButton isIcon={true} iconName={'deleteItem'} onClick={() => handleOnDelete(item.id)} />
                      {showConfirmDialog && showConfirmDialog === item.id && (
                          <ConfirmDialog
                              useWrapperClass={classNames('confirmDialog')}
                              useBtnConfirmClass={classNames('btn confirm btnConfirmDialog')}
                              handleOnConfirm={handleOnConfirmDialog}
                              handleOnCancel={handleOnCancelConfirmDialog}
                          />
                      )}
                    </td>
                )}
            {isLoading && (
                <td className={classNames('loaderWrapper')}>
                  <img className='preloader' src={preloader} alt='preloader' />
                </td>
            )}
          </tr>
        </tbody>
      </table>

    )
  }

  return (
    <div className={'sortableTable'} style={{height: virtualized ? '100%' : (items?.length || 0) * 50}}>
    {!!items && !!items.length && model && (
      <AutoSizer>
        {({ height, width }) => {
          return (
            <List
              className={classNames('listContent')}
              width={width}
              height={virtualized ? height : (items.length * 50)}
              rowHeight={50}
              rowRenderer={rowRender}
              rowCount={items.length}
              overscanRowCount={5}
            />
          );
        }}
      </AutoSizer>
      )}
    </div>
  );
};

export default withRouter(withTranslation()(EditableTable));
